/**
 * v4.30 — PortalLogoUpload.
 *
 * Shared upload widget for organization OR brand portal logos.
 * Reuses the same `FormData` multipart POST pattern as
 * `VoucherSettings` logo upload — no new infrastructure.
 *
 * Renders a calm row with two upload slots (light + dark) and live
 * previews against BOTH a white surface (light-mode shell) and a
 * dark slate surface (dark-mode shell) so the operator sees exactly
 * what each variant will look like in production.
 *
 * Props:
 *   endpoint   — e.g. "/operational-base/organizations/<id>/portal-logo"
 *                  or  "/operational-base/brands/<id>/portal-logo"
 *                Required.
 *   currentLight — current light logo_url (or null)
 *   currentDark  — current dark logo_url (or null)
 *   onUploaded   — callback({variant, logo_url}) so parent can update
 *                  its local form state without a full refetch.
 *   onRemoved    — optional callback({variant}) when delete is offered.
 *   deleteEndpoint — optional prefix for DELETE
 *                    (e.g. "/operational-base/organizations/<id>/portal-logo")
 *                    — appended with "/<variant>".
 *   disabled    — bool — disables both upload buttons (e.g. before
 *                 the parent record is saved/created).
 *   disabledMessage — explainer shown when disabled.
 */
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import api from '../../lib/api';
import {
    notifyBrandingChanged,
    setPreviewOverride,
    clearPreviewOverride,
    getPreviewOverride,
    isPreviewOverrideActive,
} from '../../lib/branding';
import ShellPreviewCard from './ShellPreviewCard';

const _MAX_BYTES = 5 * 1024 * 1024;
const _ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

const PreviewBox = ({ logoUrl, tone, label }) => {
    const { t } = useTranslation();
    const bg = tone === 'dark'
        ? '#1e293b' /* slate-800 — matches dark shell */
        : '#ffffff';
    const ringColor = tone === 'dark'
        ? 'rgba(148, 163, 184, 0.22)'
        : 'rgba(15, 23, 42, 0.10)';
    return (
        <div className="flex flex-col items-center gap-1.5">
            <div
                className="rounded-lg flex items-center justify-center"
                style={{
                    width: 96,
                    height: 56,
                    background: bg,
                    border: `1px solid ${ringColor}`,
                }}
                data-testid={`portal-logo-preview-${tone}`}
            >
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt={`${tone} preview`}
                        style={{
                            maxHeight: 40,
                            maxWidth: 88,
                            width: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                            background: 'transparent',
                        }}
                    />
                ) : (
                    <span style={{
                        fontSize: 10,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: tone === 'dark' ? '#64748b' : '#94a3b8',
                    }}>
                        {t('portalIdentity.preview.empty')}
                    </span>
                )}
            </div>
            <span className="text-[10px] tracking-[0.12em] uppercase text-slate-500 dark:text-slate-400">
                {label}
            </span>
        </div>
    );
};

const UploadSlot = ({ variant, endpoint, deleteEndpoint, currentUrl, onUploaded, onRemoved, disabled }) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; /* allow re-uploading the same file later */
        if (!file) return;
        if (file.size > _MAX_BYTES) {
            toast.error(t('portalIdentity.toast.tooBig'));
            return;
        }
        if (!file.type.startsWith('image/')) {
            toast.error(t('portalIdentity.toast.notImage'));
            return;
        }
        const fd = new FormData();
        fd.append('logo', file);
        fd.append('variant', variant);
        try {
            setUploading(true);
            const res = await api.post(endpoint, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            notifyBrandingChanged();
            onUploaded?.({ variant, logo_url: res.data?.logo_url });
            toast.success(t(
                variant === 'dark'
                    ? 'portalIdentity.toast.darkUpdated'
                    : 'portalIdentity.toast.lightUpdated'
            ));
        } catch (err) {
            const msg = err?.response?.data?.detail || t('portalIdentity.toast.uploadFailed');
            toast.error(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!deleteEndpoint) return;
        try {
            await api.delete(`${deleteEndpoint}/${variant}`);
            notifyBrandingChanged();
            onRemoved?.({ variant });
            toast.success(t(
                variant === 'dark'
                    ? 'portalIdentity.toast.darkRemoved'
                    : 'portalIdentity.toast.lightRemoved'
            ));
        } catch (err) {
            toast.error(t('portalIdentity.toast.removeFailed'));
        }
    };

    const uploadLabel = uploading
        ? t('portalIdentity.upload.uploading')
        : currentUrl
            ? t(variant === 'dark'
                ? 'portalIdentity.upload.replaceDark'
                : 'portalIdentity.upload.replaceLight')
            : t(variant === 'dark'
                ? 'portalIdentity.upload.uploadDark'
                : 'portalIdentity.upload.uploadLight');

    return (
        <div className="flex items-center gap-3">
            <input
                ref={inputRef}
                type="file"
                accept={_ACCEPT}
                onChange={handleFile}
                style={{ display: 'none' }}
                data-testid={`portal-logo-input-${variant}`}
            />
            {disabled ? (
                // v4.34.8 — When uploads are gated (new-org flow before
                // create), render a CLEARLY non-interactive lock pill
                // instead of a faded button. Previous treatment was just
                // `disabled:opacity-50` on a normal button — too subtle in
                // dark mode on iPad, operator perceived a dead button and
                // filed a bug report. Now the disabled state is visually
                // unambiguous: dashed border, lock icon, plain-language
                // label, no hover affordance, default cursor.
                <div
                    role="note"
                    aria-label={t('portalIdentity.upload.saveFirstAria', 'Save organization first to enable uploads')}
                    title={t('portalIdentity.upload.saveFirstTooltip', 'Save the organization first, then reopen it to upload portal logos.')}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border border-dashed border-amber-400/60 dark:border-amber-500/50 bg-amber-50/60 dark:bg-amber-900/15 text-amber-800 dark:text-amber-200 cursor-default select-none"
                    data-testid={`portal-logo-upload-btn-${variant}`}
                    data-disabled="true"
                >
                    <Lock className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>
                        {variant === 'dark'
                            ? t('portalIdentity.upload.saveFirstDark', 'Save first to upload dark logo')
                            : t('portalIdentity.upload.saveFirstLight', 'Save first to upload light logo')}
                    </span>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="px-3 py-1.5 rounded-md text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`portal-logo-upload-btn-${variant}`}
                >
                    {uploadLabel}
                </button>
            )}
            {currentUrl && deleteEndpoint && !disabled && (
                <button
                    type="button"
                    onClick={handleRemove}
                    className="text-xs text-slate-500 hover:text-red-600 underline-offset-2 hover:underline"
                    data-testid={`portal-logo-remove-btn-${variant}`}
                >
                    {t('portalIdentity.upload.remove')}
                </button>
            )}
        </div>
    );
};

export const PortalLogoUpload = ({
    endpoint,
    deleteEndpoint,
    currentLight,
    currentDark,
    onUploaded,
    onRemoved,
    disabled = false,
    /* "org" → title/description tuned for organization-edit modal.
     * "brand" → title/description tuned for brand-edit modal. */
    kind = 'org',
    /* v4.30.1 — Live preview context. When the parent passes
     * `previewOrgName`/`previewOrgId`, a "Preview live shell" button
     * appears and lets the operator render every shell surface (real
     * sidebar, real top bar, route navigation) as if logged into this
     * org — without saving. Optional; without these props only the
     * embedded mini-shell preview is shown. */
    previewOrgId = null,
    previewOrgName = null,
    previewBrandName = null,
}) => {
    const { t } = useTranslation();
    const title = t(kind === 'brand'
        ? 'portalIdentity.section.brandTitle'
        : 'portalIdentity.section.title');
    const description = t(kind === 'brand'
        ? 'portalIdentity.section.brandDescription'
        : 'portalIdentity.section.description');
    const disabledMessage = !disabled ? null : t(kind === 'brand'
        ? 'portalIdentity.section.saveBrandFirst'
        : 'portalIdentity.section.saveOrgFirst');
    /* Subscribe to global override state so the "Exit preview" button
     * also reflects external clears (e.g. banner click). */
    const [livePreviewActive, setLivePreviewActive] = useState(
        () => isPreviewOverrideActive() &&
              getPreviewOverride()?.organization?.id === previewOrgId
    );
    useEffect(() => {
        const sync = () => {
            const o = getPreviewOverride();
            setLivePreviewActive(
                isPreviewOverrideActive() && o?.organization?.id === previewOrgId
            );
        };
        window.addEventListener('ob:preview-override-changed', sync);
        return () => window.removeEventListener('ob:preview-override-changed', sync);
    }, [previewOrgId]);

    /* The payload the in-modal mini-shell renders right now. Always
     * uses the LATEST uploaded URLs from props so hot-update is free
     * (after an upload the parent setState updates currentLight /
     * currentDark which flows straight into both this card and the
     * live preview override below). */
    const previewPayload = {
        logo_url: currentLight || null,
        logo_dark_url: currentDark || null,
        logo_source: (currentLight || currentDark) ? 'org' : 'platform',
        logo_dark_source: (currentDark || currentLight) ? 'org' : 'platform',
        organization: previewOrgId
            ? { id: previewOrgId, name: previewOrgName, display_name: previewOrgName }
            : null,
        brand_name: previewBrandName,
        demo_mode: false,
    };

    /* Hot-update the live preview override whenever a new logo lands
     * while preview is active — survives the round-trip without a
     * manual refresh. */
    useEffect(() => {
        if (livePreviewActive) {
            setPreviewOverride(previewPayload);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('ob:preview-override-changed'));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLight, currentDark, livePreviewActive]);

    const handleStartLivePreview = () => {
        if (!previewOrgId) return;
        setPreviewOverride(previewPayload);
        setLivePreviewActive(true);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ob:preview-override-changed'));
        }
        toast.success(t('portalIdentity.toast.livePreviewStarted', {
            org: previewOrgName || t('portalIdentity.banner.defaultOrgLabel'),
        }));
    };

    const handleStopLivePreview = () => {
        clearPreviewOverride();
        setLivePreviewActive(false);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ob:preview-override-changed'));
        }
    };

    return (
        <div
            className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-5"
            data-testid="portal-logo-upload"
        >
            {/* ── Section header ─────────────────────────────────── */}
            <div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {title}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    {description}
                </p>
            </div>

            {disabled && disabledMessage && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                    {disabledMessage}
                </p>
            )}

            {/* ── Helper explanation (v4.30.3 — operator clarity) ──
              * Calm, plain-language explanation of the two variants
              * BEFORE the operator has to make any upload decision. */}
            <div
                className="rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3 space-y-1.5"
                data-testid="portal-logo-helper"
            >
                <div className="text-[11px] font-semibold tracking-wide uppercase text-slate-600 dark:text-slate-300">
                    {t('portalIdentity.helper.title')}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t('portalIdentity.helper.lightExplain')}
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t('portalIdentity.helper.darkExplain')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-500 leading-relaxed italic">
                    {t('portalIdentity.helper.oneIsEnough')}
                </p>
            </div>

            {/* ── Visually-dominant in-modal mini-shell preview ────
              * Operator sees the outcome BEFORE the upload buttons —
              * this is the "this is what users will see" surface. */}
            <ShellPreviewCard payload={previewPayload} />

            {/* ── Single-logo state hint (v4.30.3) ────────────────
              * Calm reassurance when only one variant is uploaded so
              * the operator doesn't feel they're missing a required
              * second upload. */}
            {(currentLight || currentDark) && (
                <p
                    className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed"
                    data-testid="portal-logo-coverage-hint"
                >
                    {currentLight && currentDark
                        ? t('portalIdentity.helper.bothUploaded')
                        : t('portalIdentity.helper.usedInBothModes')}
                </p>
            )}

            {/* ── Upload slots ─────────────────────────────────── */}
            <div className="space-y-2">
                {/* v4.34.8 — Repeat the "save first" banner directly above
                    the upload controls when gated. The top-of-section
                    banner is too far away on long forms — operators scroll
                    past it and hit the disabled state without context. */}
                {disabled && disabledMessage && (
                    <div
                        className="rounded-md border border-amber-300/70 dark:border-amber-600/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
                        data-testid="portal-logo-upload-gated-banner"
                    >
                        <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                            {disabledMessage}
                        </p>
                    </div>
                )}
                <UploadSlot
                    variant="light"
                    endpoint={endpoint}
                    deleteEndpoint={deleteEndpoint}
                    currentUrl={currentLight}
                    onUploaded={onUploaded}
                    onRemoved={onRemoved}
                    disabled={disabled}
                />
                <UploadSlot
                    variant="dark"
                    endpoint={endpoint}
                    deleteEndpoint={deleteEndpoint}
                    currentUrl={currentDark}
                    onUploaded={onUploaded}
                    onRemoved={onRemoved}
                    disabled={disabled}
                />
            </div>

            <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">
                {t('portalIdentity.section.fileHint')}
            </p>

            {/* ── Preview Portal action (v4.30.3 — promoted CTA) ────
              * Renamed from "Preview live shell" to "Preview Portal"
              * and visually elevated so the operator can't miss it. */}
            {previewOrgId && (
                <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                    {!livePreviewActive ? (
                        <button
                            type="button"
                            onClick={handleStartLivePreview}
                            disabled={disabled}
                            className="px-4 py-2 rounded-md text-sm font-semibold bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                            data-testid="portal-logo-start-live-preview"
                        >
                            <span
                                className="inline-block rounded-full"
                                style={{ width: 6, height: 6, background: '#10b981' }}
                                aria-hidden="true"
                            />
                            {t('portalIdentity.livePreview.start')}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleStopLivePreview}
                            className="px-4 py-2 rounded-md text-sm font-semibold border border-slate-400 dark:border-slate-500 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-600"
                            data-testid="portal-logo-stop-live-preview"
                        >
                            {t('portalIdentity.livePreview.stop')}
                        </button>
                    )}
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed flex-1 min-w-[200px]">
                        {t('portalIdentity.livePreview.helper')}
                    </span>
                </div>
            )}
        </div>
    );
};

export default PortalLogoUpload;
